import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatMoney, toCents } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/** Devengo del contratista por avance certificado. Nunca por encima de lo disponible. */
export const CertificationDialog = ({ open, onOpenChange, projectId, contratistas = [] }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [subId, setSubId] = useState("");
  const [monto, setMonto] = useState("");
  const [descripcion, setDescripcion] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubId(contratistas[0]?.sub_id || ""); setMonto(""); setDescripcion("");
  }, [open, contratistas]);

  const seleccionado = contratistas.find((c) => c.sub_id === subId);
  const disponible = seleccionado?.disponible_para_certificar_cents ?? 0;

  const mutation = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/certifications`, {
      sub_id: subId, monto_cents: toCents(monto), descripcion,
    }),
    onSuccess: () => {
      toast.success(t("app.saved"));
      qc.invalidateQueries({ queryKey: ["project-detail", projectId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const blocked = !subId || toCents(monto) <= 0 || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="certification-dialog" className="max-w-md rounded-[10px]">
        <DialogHeader>
          <DialogTitle>{t("projects.certifications.add")}</DialogTitle>
        </DialogHeader>

        <div>
          <Label className="text-[12.5px]">{t("projects.certifications.sub")} *</Label>
          <Select value={subId} onValueChange={setSubId}>
            <SelectTrigger data-testid="certification-sub-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {contratistas.map((c) => <SelectItem key={c.sub_id} value={c.sub_id}>{c.sub_nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          {seleccionado && (
            <p className="mt-1 text-[11.5px] text-ink-secondary" data-testid="certification-available">
              {t("projects.detail.available")}: {formatMoney(disponible)}
            </p>
          )}
        </div>
        <div>
          <Label className="text-[12.5px]">{t("projects.certifications.amount")} *</Label>
          <Input data-testid="certification-amount-input" value={monto} placeholder="0.00" onChange={(e) => setMonto(e.target.value)} />
        </div>
        <div>
          <Label className="text-[12.5px]">{t("projects.certifications.description")}</Label>
          <Textarea data-testid="certification-description-input" rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>

        <DialogFooter>
          <Button data-testid="certification-submit-button" disabled={blocked} onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("app.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
